<template>
    <div class="text-editor-wrapper" :style="{ width: isRichInputBig ? '100%' : '500px', height: isRichInputBig ? '500px' : '226px' }">
        <div class="text-textarea-wrapper">
            <RichInput
                :class="{
                  'text-input': true,
                  'rtl': isArabic
                }"
                ref="textInput"
                :placeholder="placeholder"
                :value="inputHtml"
                :maxlength="maxlength || 2000"
                @change="changeHandler"
                @range="rangeHandler"
                @mouseAt="mouseAtHandler"
                @clickAt="clickAtHandler"
                @cursor="cursorHandler"
                @wordcount="wordCountHandler"
            />
            <span class="textarea-tip">{{ wordCount }}/{{ maxlength || 2000 }}</span>
        </div>
        <TextOperateBar
            :disabled="computedStatus"
            :disabledCmd="!hasRange"
            :hiddenBtns="hiddenBtns"
            :isRichInputBig="isRichInputBig"
            @showEmoji="showEmoji"
            @itemClick="itemClick"
            @clearStyle="clearStyle"
            @magnify="magnify"
        />
        <Emoji v-if="isShowEmoji" class="emoji" @emojiHandle="emojiHandle"></Emoji>
        <component
            :is="tips.component"
            v-if="tips.component"
            :position="tips.position"
            algin="middle"
            :message="tips.message"
            @mouseover="mouseOverTips"
            @mouseout="mouseOutTips"
        />
    </div>
</template>

<script>
import RichInput from "@/views/zhiji/bComponents/inputs/RichInput";
import Emoji from "@/views/zhiji/bComponents/emoji/Emoji";
import TextOperateBar from "./TextOperateBar";
import linkConfig from "./linkConfig";
import questionConfig from "./questionConfig";
import hightlightConfig from "./hightlightConfig";
import portraitConfig from "./portraitConfig";
import { isArabicText } from "@/views/zhiji/js/common/util.js";

// 支持操作的集合
const configs = new Map();
configs.set(linkConfig.type, linkConfig);
configs.set(questionConfig.type, questionConfig);
configs.set(hightlightConfig.type, hightlightConfig);
configs.set(portraitConfig.type, portraitConfig);

export default {
    name: "TextEditor",
    components: {
        RichInput,
        Emoji,
        TextOperateBar
    },
    props: ["value", "placeholder", "hiddenBtns", "maxlength", "language"],
    data() {
        return {
            isShowEmoji: false,
            wordCount: 0,
            selectedText: "",
            selectedId: "",
            hasRange: false,
            colorData: "", // TODO怎么抽出去
            wordNodes: new Map(),
            cmdNodes: new Map(),
            isArabic: false,
            isRichInputBig: false,
            tips: {
                component: null,
                position: {},
                message: ""
            }
        };
    },
    computed: {
        computedStatus() {
            return !this.selectedText;
        },
        inputHtml() {
            return this.convertToHtml(this.value);
        },
        isOld() {
            const { gameId } = this.$store.state.zhiji.gameState;
            const oldGameIds = [];
            return oldGameIds.some(gId => gId == gameId);
        },
    },
    created() {
        document.addEventListener("click", this.handleDocumentClick);
    },
    destroyed() {
        window.document.removeEventListener("click", this.handleDocumentClick);
    },
    methods: {
        // text + linkword转换成html；命令字转换
        convertToHtml({ text = '', linkWord = [], cmd = [], cmdTagClassify = [] } = {}) {
            let html = text.replace(/\r\n/g, "<br>");
            html = html.replace(/\n/g, "<br>");
            linkWord.forEach(w => {
                const type = w.type;
                const config = configs.get(type);
                const wordNode = config.fromOut(w);
                this.wordNodes.set(wordNode.id, wordNode);
                html = html.replace(`{{${w.id}}}`, config.toHtml(wordNode));
            });
            [...cmd, ...cmdTagClassify].forEach(c => {
                const config = portraitConfig;
                const cmdNode = config.fromOut(c);
                this.cmdNodes.set(cmdNode.id, cmdNode);
            });
            this.isArabic = isArabicText(html);
            return html;
        },
        // html转换成 text + linkword；过滤命令参数
        convertToText(dom) {
            let linkWord = [];
            let text = ""; // 文本
            const cmd = []; // 画像参数
            const cmdTagClassify = []; // 画像标签

            const params = dom.innerText.match(/[^{\}]+(?=})/g) || [];
            const tags = dom.innerText.match(/[^[\]]+(?=])/g) || [];

            const cmdConfig = portraitConfig;
            for (const [, cmdNode] of this.cmdNodes) {
                if (cmdNode.type === "param" && params.indexOf(cmdNode.key) > -1) {
                    cmd.push(cmdConfig.toOut(cmdNode));
                } else if (cmdNode.type === "tag" && tags.indexOf(cmdNode.key) > -1) {
                    cmdTagClassify.push(cmdConfig.toOut(cmdNode));
                }
            }

            dom.childNodes.forEach(item => {
                if (item.id && item.nodeName !== "DIV") {
                    if (item.textContent.length === 0) {
                        console.log("【text editor】empty node");
                        return;
                    }
                    const wordNode = this.wordNodes.get(item.id);
                    // 其他地方粘贴过来的，不做处理
                    if (!wordNode) {
                        text += item.textContent;
                        return;
                    }
                    wordNode.word = item.textContent;
                    const config = configs.get(wordNode.type);
                    const dataObj = config.toOut(wordNode);

                    linkWord.push(dataObj);
                    text += "{{" + item.id + "}}";

                    // highlight这种可编辑的节点，点击文字最后换行时，换行符会加到这个节点下面，不会加到这个节点后面，做一下兼容
                    if (item.innerHTML.endsWith("<br>")) {
                        text += "\n";
                    }
                } else if (item.nodeName == "DIV") {
                    let child = this.convertToText(item);
                    text += "\n" + child.text;
                    linkWord = [...linkWord, ...child.linkWord];
                } else if (item.nodeName == "BR") {
                    text += "\n";
                } else {
                    text += item.textContent;
                }
            });
            text = text.replace(/\n$/g, ""); // 去除文本结尾原本存在的换行

            return { text, linkWord, cmd, cmdTagClassify };
        },
        // 组件内容变更，触发change事件到上级
        changeHandler(editedDom) {
            const { text, linkWord, cmd, cmdTagClassify } = this.convertToText(editedDom);

            this.$emit("change", {
                text,
                linkWord: linkWord.filter(item => item.word),
                cmd,
                cmdTagClassify,
                isOld: this.isOld,
            });
        },
        // 选中区域变更
        rangeHandler({ text, id }) {
            if (text.trim() === "") {
                this.selectedText = "";
                this.selectedId = "";
                this.hasRange = true;
                return;
            }
            this.selectedText = text;
            this.selectedId = id;
            this.hasRange = true;
        },
        // TODO，之后放开的时候要抽到config里面
        emojiHandle(data) {
            this.$refs.textInput.insertAtSeletionRange(
                {
                    tag: "i",
                    innerText: data.imgCode,
                    editable: false,
                    attrs: [{ "data-type": "emoj" }]
                },
                true
            );
            this.isShowEmoji = false;
        },
        showEmoji() {
            this.isShowEmoji = !this.isShowEmoji;
        },
        clearStyle() {
            const { text = "", linkWord = [] } = this.value;
            let newText = text;
            linkWord.forEach(w => {
                newText = newText.replace(`{{${w.id}}}`, w.word);
            });
            this.$emit("change", {
                text: newText,
                linkWord: [],
                cmd: [],
                cmdTagClassify: [],
                isOld: this.isOld,
            });
        },
        magnify() {
         this.$emit('magnify')
         this.isRichInputBig = !this.isRichInputBig
        },
        // 点击高亮/链接这些按钮时触发的事件
        itemClick({ type, event }) {
            const config = configs.get(type);

            // 没有定义的类型不做处理
            if (!config) {
                return;
            }

            const selectedNode = this.getNodeDataById(this.selectedId);
            config.editorHandler.call(
                this,
                this.$store,
                { operate: "add", word: this.selectedText, selectedNode, event, language: this.language },
                ({ elem, node, operate }) => {
                    // 部分类型重复操作表示删除，比如highlight
                    if (operate === "delete") {
                        this.deleteNodeData(elem.id);
                        this.$refs.textInput.replaceNodeWithContent(elem);
                        return;
                    }

                    // 插入的时候，允许被插入的节点类型做一些操作，比如hightlight分割
                    let splitNodes = null;
                    if (selectedNode && configs.get(selectedNode.type).beforeInsert) {
                        splitNodes = configs.get(selectedNode.type).beforeInsert(selectedNode, node);
                    }

                    // 有做分割的情况下，按位置添加节点
                    if (splitNodes) {
                        const { before, after } = splitNodes;
                        this.deleteNodeData(selectedNode.id); // 删除原来的可编辑节点
                        const nodes = [...before.node, node, ...after.node];
                        const elems = [...before.elem, elem, ...after.elem];
                        nodes.forEach(n => this.insertNodeData(n));
                        this.$refs.textInput.replaceElementById(selectedNode.id, elems, config.toEnd);
                        return;
                    }

                    // 判断插入数据类型
                    this.insertNodeData(node);

                    this.$refs.textInput.insertAtSeletionRange(elem, config.toEnd);
                }
            );
            this.selectedText = "";
            this.selectedId = "";
        },
        insertNodeData(node) {
            // 判断插入数据类型
            if (node.cmdNode) {
                this.cmdNodes.set(node.id, node);
            } else {
                this.wordNodes.set(node.id, node);
            }
        },
        getNodeDataById(id) {
            return this.wordNodes.get(id) || this.cmdNodes.get(id);
        },
        deleteNodeData(id) {
            if (this.cmdNodes.has(id)) {
                this.cmdNodes.delete(id);
            }
            if (this.wordNodes.has(id)) {
                this.wordNodes.delete(id);
            }
        },
        handleDocumentClick(e) {
            if (!this.$el.contains(e.target)) {
                this.isShowEmoji = false;
            }
        },
        // 鼠标放到元素上，可以展示配置的提示框
        mouseAtHandler({ id, position }) {
            if (!id) {
                this.hiddenTips();
                return;
            }
            const node = this.getNodeDataById(id);
            const config = configs.get(node.type);
            if (!config) {
                return;
            }
            this.tips = {
                position,
                message: config.getTips(node),
                component: config.tipsComponent
            };
        },
        // 鼠标在tips弹框上面，延迟清除tips
        mouseOverTips() {
            this.tipsTimeout && clearTimeout(this.tipsTimeout);
        },
        // 鼠标离开tips弹框
        mouseOutTips() {
            this.hiddenTips();
        },
        // 清理tips弹框
        hiddenTips() {
            this.tipsTimeout && clearTimeout(this.tipsTimeout);
            this.tipsTimeout = setTimeout(() => {
                this.tips = {
                    position: {},
                    message: "",
                    component: null
                };
            }, 300);
        },
        // 点击元素，对具体的类型进行编辑删除
        clickAtHandler({ id }) {
            const node = this.getNodeDataById(id);
            const config = configs.get(node.type);
            if (!config) {
                return;
            }

            config.editorHandler.call(this, this.$store, { ...node, operate: "update", language: this.language }, ({ elem, node, operate }) => {
                if (operate === "delete") {
                    this.deleteNodeData(id);
                    this.$refs.textInput.replaceNodeWithContent(elem);
                    return;
                }
                this.insertNodeData(node);
                elem && this.$refs.textInput.updateNode(elem, true);
            });
        },
        // 需要根据光标位置的字符进行输入提示
        cursorHandler({ char, position }) {
            for (let [, config] of configs) {
                let isHandled = false; // 是否有类型处理了标识
                if (config.triggerChar) {
                    isHandled = config.triggerChar.call(this, this.$store, { char, position }, ({ elem, node }) => {
                        this.insertNodeData(node);

                        this.$refs.textInput.insertAtSeletionRange(elem, config.toEnd);
                    });
                }
                if (isHandled) {
                    break;
                }
            }
        },
        wordCountHandler(count) {
            this.wordCount = count;
        }
    }
};
</script>

<style lang="less" scoped>
.text-editor-wrapper {
    position: relative;
    width: 500px;
    height: 226px;
    display: flex;
    flex-direction: column;
}
.text-textarea-wrapper {
    flex: 1;
    position: relative;
    background: #fafbfd;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    overflow-y: auto;
}
.text-input {
    flex: 1;
    padding: 10px 10px 24px;
    background: transparent;
    vertical-align: top;
    box-sizing: border-box;
}
.emoji {
    position: absolute;
    bottom: -200px;
    left: 0;
    z-index: 1;
}
.textarea-tip {
    position: absolute;
    bottom: 4px;
    right: 8px;
    font-size: 12px;
    color: #9b9ba3;
}
.rtl {
  direction: rtl;
  font-size: 18px;
}
</style>
